import {
  DetailCard,
  DetailCardLink,
  type DetailCardRow,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@civilgrid/ui/components';
import { useSnapshot } from 'valtio';
import { requestTypeLabelMap } from '~/common/dataRequestUtils';
import type { CivilGridFile } from '~/common/types';
import type { UtilityAgency } from '../common';
import { downloadAndTrackUtilitiesFile } from '../common/trackUtilitiesFileDownload';
import store from '../store';
import { getUtilityRowId, requestMatchesRow } from './grouping';

interface Props {
  agency: UtilityAgency;
}

export default function DocumentsCell({ agency }: Props) {
  const { dataRequests, project } = useSnapshot(store) as typeof store;

  const requests = Object.values(dataRequests).filter((d) => requestMatchesRow(d, agency));

  const filesAvailable = requests.some((r) => r.files.length > 0);

  const onFileDownload = (file: CivilGridFile) => downloadAndTrackUtilitiesFile(file, 'dataRequest', project?.id);

  if (filesAvailable) {
    // One row per request type (DetailCard keys rows by label), its files listed as download links.
    const filesByType = new Map<string, CivilGridFile[]>();
    for (const r of requests) {
      if (r.files.length === 0) continue;
      const label = requestTypeLabelMap[r.type];
      filesByType.set(label, [...(filesByType.get(label) ?? []), ...r.files]);
    }
    const rows: DetailCardRow[] = [...filesByType].map(([label, files]) => ({
      label,
      value: (
        <div className="flex flex-col items-start gap-1">
          {files.map((f) => (
            <DetailCardLink
              key={f.id}
              icon="download"
              onClick={() => onFileDownload(f)}
              data-testid={`utilities-file-download-${f.id}`}
            >
              {f.name}
            </DetailCardLink>
          ))}
        </div>
      ),
    }));

    // Opens on hover or click; a click keeps it open, so its download links can be reached by keyboard.
    const label = `Files from ${agency.agencyName}`;
    return (
      <Popover openOnHover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className="inline-flex cursor-pointer rounded-sm py-1 focus-ring"
            // The compact table opens Details on a row click; opening the files shouldn't.
            onClick={(e) => e.stopPropagation()}
            data-testid={`utilities-files-trigger-${getUtilityRowId(agency)}`}
          >
            <Icon icon="file" size={18} />
          </button>
        </PopoverTrigger>
        <PopoverContent aria-label={label} className="border-0 bg-transparent p-0 shadow-none">
          <DetailCard rows={rows} className="w-72" />
        </PopoverContent>
      </Popover>
    );
  }
  return null;
}
